import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GameStateResponse } from '@/lib/api';

interface UseGameStatesReturn {
  states: GameStateResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGameStates(): UseGameStatesReturn {
  const [states, setStates] = useState<GameStateResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchStates = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.listGameStates({ limit: 100 });
      if (fetchIdRef.current !== id) return;
      setStates(result.states);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load game states');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchStates();
  }, [fetchStates]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { states, total, loading, error, refresh };
}
